import { uiText } from "@/lib/i18n/uiText";
import { useMemo } from "react";
import { Link, useLocation, useParams } from "@/lib/router-compat";
import { Loader2, Clock, Target, TrendingUp, Gauge, ShieldCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { LegalDisclaimer } from "@/components/shared/LegalDisclaimer";
import { CrossSportLinks } from "@/components/seo/CrossSportLinks";
import { SportHubLinks } from "@/components/seo/SportHubLinks";
import { HOME_CRUMB } from "@/lib/breadcrumbs";
import { RevealGate } from "@/components/predictions/RevealGate";
import { useSEO } from "@/lib/useSEO";
import { useLocalizedFields } from "@/hooks/useLocalizedContent";
import { useSportFixture, useSportFixtureSiblings } from "@/hooks/useSportFixture";
import { findUsSport, ogImageForUsSport } from "@/lib/usSports";
import {
  breadcrumbJsonLd,
  faqPageJsonLd,
  articleJsonLd,
  usSportsEventJsonLd,
  usSportSchemaName,
} from "@/lib/seoSchema";
import {
  buildSportFixtureSlug,
  marketLabel,
  matchTitle,
  outcomeWithLine,
  primaryRow,
  sportFixtureEditorial,
  sportFixtureFaqs,
  sportFixtureMeta,
  sportFixturePathFor,
  editorialWordCount,
} from "@/lib/sportFixtures";
import NotFound from "./NotFound";

/**
 * Individual match page for every non-football sport
 * (`/<sport>-predictions/match/<home>-vs-<away>-<date>`).
 *
 * Each page is a self-contained, citable resource: full market table, 400+
 * words of data-grounded analysis, contextual FAQs, SportsEvent + Article +
 * FAQPage structured data, and internal links back into the sport's hub. All
 * prose is translated on demand and cached in the database so the same match is
 * never re-translated for a returning visitor.
 */
export default function SportFixtureDetail() {
  const location = useLocation();
  const { slug } = useParams();

  const sportSlug = location.pathname.split("/")[1]?.replace("-predictions", "") ?? "";
  const sport = findUsSport(sportSlug);

  const { data: rows, isLoading } = useSportFixture(sportSlug, slug);
  const { data: siblings } = useSportFixtureSiblings(sportSlug, slug);

  const anchor = rows && rows.length ? primaryRow(rows) : null;

  const meta = useMemo(
    () => (rows && rows.length ? sportFixtureMeta(rows, sport) : null),
    [rows, sport],
  );
  const sections = useMemo(
    () => (rows && rows.length ? sportFixtureEditorial(rows, sport) : []),
    [rows, sport],
  );
  const faqs = useMemo(
    () => (rows && rows.length ? sportFixtureFaqs(rows, sport) : []),
    [rows, sport],
  );

  // ---- Localisation ------------------------------------------------------
  // One batched request per match; the edge function persists every string in
  // `content_translations`, keyed by a hash of the source, so repeat visits and
  // other users in the same locale hit the cache instead of the model.
  const translatable = useMemo(() => {
    const out: Record<string, string> = {};
    if (meta) {
      out["h1"] = meta.h1;
      out["title"] = meta.title;
      out["description"] = meta.description;
    }
    sections.forEach((s, i) => {
      out[`s${i}_heading`] = s.heading;
      s.paragraphs.forEach((p, j) => {
        out[`s${i}_p${j}`] = p;
      });
    });
    faqs.forEach((f, i) => {
      out[`faq${i}_q`] = f.question;
      out[`faq${i}_a`] = f.answer;
    });
    return out;
  }, [meta, sections, faqs]);

  const fixtureKey = anchor
    ? `${anchor.sport}:${buildSportFixtureSlug(anchor.home_team, anchor.away_team, anchor.commence_time)}`
    : null;
  const { values: tr } = useLocalizedFields("sport_fixture", fixtureKey, translatable);

  const t = (key: string, fallback: string) => tr[key] || fallback;

  const localizedSections = sections.map((s, i) => ({
    heading: t(`s${i}_heading`, s.heading),
    paragraphs: s.paragraphs.map((p, j) => t(`s${i}_p${j}`, p)),
  }));
  const localizedFaqs = faqs.map((f, i) => ({
    question: t(`faq${i}_q`, f.question),
    answer: t(`faq${i}_a`, f.answer),
  }));

  const pair = anchor ? matchTitle(anchor, sport) : "";
  const canonicalPath = meta?.canonicalPath ?? location.pathname;
  const label = sport?.label ?? sportSlug.toUpperCase();

  const crumbs = [
    HOME_CRUMB,
    { name: `${label} Predictions`, path: `/${sportSlug}-predictions` },
    { name: pair || "Match", path: canonicalPath },
  ];

  useSEO({
    title: t("title", meta?.title ?? `${label} Match Prediction | Statlign`),
    description: t(
      "description",
      meta?.description ??
        `${label} match prediction, odds, model probability and value edge from Statlign.`,
    ),
    ogTitle: meta?.ogTitle!,
    ogDescription: meta?.ogDescription!,
    canonicalPath,
    image: ogImageForUsSport(sportSlug),
    imageAlt: pair ? `${pair} — ${label} prediction and odds` : `${label} predictions`,
    ogType: "article",
    keywords: meta?.keywords!,
    publishedTime: anchor?.created_at ?? undefined!,
    modifiedTime: anchor?.updated_at ?? anchor?.created_at ?? undefined!,
    jsonLd: anchor
      ? [
          breadcrumbJsonLd(crumbs),
          usSportsEventJsonLd(
            {
              home_team: anchor.home_team,
              away_team: anchor.away_team,
              commence_time: anchor.commence_time,
              odds: anchor.odds ?? null,
              predicted_outcome: outcomeWithLine(anchor),
            },
            usSportSchemaName(sportSlug),
            `https://statlign.com${canonicalPath}`,
          ),
          articleJsonLd({
            headline: meta?.h1 ?? `${pair} prediction`,
            description: meta?.description ?? "",
            path: canonicalPath,
            datePublished: anchor.created_at ?? undefined!,
            dateModified: anchor.updated_at ?? anchor.created_at ?? undefined!,
            section: `${label} Predictions`,
            keywords: meta?.keywords!,
          }),
          faqPageJsonLd(faqs),
        ]
      : undefined!,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />  {uiText("Loading match analysis…")} </div>
    );
  }

  if (!sport || !rows || rows.length === 0 || !anchor || !meta) {
    return <NotFound />;
  }

  const kickoff = new Date(anchor.commence_time);
  const wordCount = editorialWordCount(sections);

  return (
    <article className="space-y-8 animate-fade-in">
      <Breadcrumbs items={crumbs} className="text-sm text-muted-foreground" />

      <header className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="uppercase text-[10px]">
            {label}
          </Badge>
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            {kickoff.toUTCString()}
          </span>
        </div>
        <h1 className="text-3xl font-bold tracking-tight">{t("h1", meta.h1)}</h1>
        <p className="text-muted-foreground max-w-3xl">
          {t("description", meta.description)}
        </p>
      </header>

      {/* Headline pick ----------------------------------------------------- */}
      <Card className="border-primary/25 bg-gradient-to-r from-primary/10 to-primary/5">
        <CardContent className="p-5 grid gap-4 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <p className="text-xs uppercase tracking-wide text-primary">{uiText("Model selection")}</p>
            <RevealGate predictionId={anchor.id} kickoff={anchor.commence_time} evaluated={anchor.was_correct != null}>
              <p className="text-2xl font-bold">{outcomeWithLine(anchor)}</p>
            </RevealGate>
            <p className="text-xs text-muted-foreground mt-1">
              {marketLabel(sport, String(anchor.market_type))}
            </p>
          </div>
          {anchor.odds != null && (
            <div>
              <p className="text-xs text-muted-foreground">{uiText("Odds")}</p>
              <p className="text-2xl font-bold text-primary">{(anchor.odds != null ? Number(anchor.odds).toFixed(2) : "—")}</p>
            </div>
          )}
          {anchor.confidence_score != null && (
            <div>
              <p className="text-xs text-muted-foreground">{uiText("Confidence")}</p>
              <p className="text-2xl font-bold">{Math.round(anchor.confidence_score)}%</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Market table ------------------------------------------------------ */}
      <section className="space-y-3">
        <h2 className="text-xl font-semibold">{uiText("All modelled markets")}</h2>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <caption className="sr-only">
              {pair}  {uiText("— every modelled market with odds, model probability and value edge")} </caption>
            <thead className="bg-muted/50 text-left">
              <tr>
                <th scope="col" className="p-3 font-medium">{uiText("Market")}</th>
                <th scope="col" className="p-3 font-medium">{uiText("Selection")}</th>
                <th scope="col" className="p-3 font-medium">{uiText("Odds")}</th>
                <th scope="col" className="p-3 font-medium">{uiText("Model")}</th>
                <th scope="col" className="p-3 font-medium">{uiText("Implied")}</th>
                <th scope="col" className="p-3 font-medium">{uiText("Edge")}</th>
                <th scope="col" className="p-3 font-medium">{uiText("Confidence")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border/60">
                  <td className="p-3">{marketLabel(sport, String(r.market_type))}</td>
                  <td className="p-3 font-medium">
                    <RevealGate predictionId={r.id} kickoff={anchor.commence_time} evaluated={r.was_correct != null}>
                      {outcomeWithLine(r)}
                    </RevealGate>
                  </td>
                  <td className="p-3 tabular-nums">{r.odds != null ? r.odds.toFixed(2) : "—"}</td>
                  <td className="p-3 tabular-nums">
                    {r.model_probability != null ? `${Math.round(r.model_probability * 100)}%` : "—"}
                  </td>
                  <td className="p-3 tabular-nums">
                    {r.implied_probability != null
                      ? `${Math.round(r.implied_probability * 100)}%`
                      : "—"}
                  </td>
                  <td className="p-3 tabular-nums">
                    {r.edge != null ? `${(r.edge * 100).toFixed(1)}%` : "—"}
                  </td>
                  <td className="p-3 tabular-nums">
                    {r.confidence_score != null ? `${Math.round(r.confidence_score)}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <ShieldCheck className="h-3 w-3" />
           {uiText("Odds recorded at generation time. Confirm the live price with your bookmaker before staking.")} </p>
      </section>

      {/* Editorial --------------------------------------------------------- */}
      {localizedSections.map((s, i) => (
        <section key={i} className="space-y-3">
          <h2 className="text-xl font-semibold">{uiText(s.heading)}</h2>
          {s.paragraphs.map((p, j) => (
            <p key={j} className="text-muted-foreground leading-relaxed">
              {p}
            </p>
          ))}
        </section>
      ))}

      {/* Key metrics summary ---------------------------------------------- */}
      <section className="space-y-3">
        <h2 className="text-xl font-semibold">{uiText("Key takeaways")}</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="p-3 rounded-lg bg-muted/40 border border-border/50">
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Target className="h-3 w-3 text-primary" />  {uiText("Headline pick")} </p>
            <RevealGate predictionId={anchor.id} kickoff={anchor.commence_time} evaluated={anchor.was_correct != null}>
              <p className="font-semibold mt-1">{outcomeWithLine(anchor)}</p>
            </RevealGate>
          </div>
          <div className="p-3 rounded-lg bg-muted/40 border border-border/50">
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <TrendingUp className="h-3 w-3 text-primary" />  {uiText("Best edge")} </p>
            <p className="font-semibold mt-1">
              {rows.some((r) => r.edge != null)
                ? `${(Math.max(...rows.map((r) => r.edge ?? 0)) * 100).toFixed(1)}%`
                : "—"}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-muted/40 border border-border/50">
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Gauge className="h-3 w-3 text-primary" />  {uiText("Markets modelled")} </p>
            <p className="font-semibold mt-1">{rows.length}</p>
          </div>
        </div>
      </section>

      {/* FAQs -------------------------------------------------------------- */}
      <section className="space-y-3">
        <h2 className="text-xl font-semibold">{pair}  {uiText("— frequently asked questions")}</h2>
        <dl className="space-y-4">
          {localizedFaqs.map((f, i) => (
            <div key={i}>
              <dt className="font-medium">{uiText(f.question)}</dt>
              <dd className="text-muted-foreground mt-1">{uiText(f.answer)}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Internal links ---------------------------------------------------- */}
      {siblings && siblings.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-xl font-semibold">{uiText("More")} {label}  {uiText("match predictions")}</h2>
          <ul className="grid gap-2 sm:grid-cols-2">
            {siblings.map((s) => (
              <li key={s.id}>
                <Link
                  to={sportFixturePathFor(s)}
                  className="text-sm text-primary hover:underline"
                >
                  {matchTitle(s, sport)}  {uiText("prediction")} </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <nav aria-label={uiText("Related pages")} className="flex flex-wrap gap-3 text-sm">
        <Link to={`/${sportSlug}-predictions`} className="text-primary hover:underline">
           {uiText("All")} {label}  {uiText("predictions")} </Link>
        <Link to="/track-record" className="text-primary hover:underline">
           {uiText("Verified track record")} </Link>
        <Link to="/how-it-works" className="text-primary hover:underline">
           {uiText("How the model works")} </Link>
        <Link to="/guides/value-betting" className="text-primary hover:underline">
           {uiText("Value betting guide")} </Link>
      </nav>

      {/* Same link spine every other page of this sport carries, so a match page
          reached from search leads on to the day, weekend, team and market pages. */}
      {sport && <SportHubLinks sport={sport} current="fixture" />}

      <CrossSportLinks currentSport={sportSlug} />

      <LegalDisclaimer />

      {/* Non-rendered depth signal for the build-time content validator. */}
      <meta data-content-words={String(wordCount)} />
    </article>
  );
}
